import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { useUserDetails } from '../hooks/userDetails';
import { Role } from '../models';
import PrivateRoute from '../modules/profile/components/PrivateRoute';
import CoordinatorResit from '../pages/resit/CoordinatorResitFlow';
import CoordinatorResitRegister from '../pages/resit/CoordinatorResitRegister';
import Instruction from '../pages/resit/Instruction';
import Receipt from '../pages/resit/Receipt';
import ResitFlow from '../pages/resit/ResitFlow';
import Summary from '../pages/resit/Summary';
import { parseLocationSearch } from '../utils/StringUtils';
import RouteKey from './key';
import { usePrevious } from '../hooks/usePrevious';
import Listing from '../pages/resit/Listing';

const {
  eligible,
  listing,
  instruction,
  resitFlow,
  receipt,
  summary,
  coordinatorListing,
  coordinatorResitRegister,
  coordinatorResitFlow,
} = RouteKey.resit.child;

export const ResitNavigationContext = React.createContext<{
  toEligible: string;
  toInstruction: string;
  toResitFlow: string;
  toReceipt: string;
  toSummary: string;
  toCoordinatorResitRegister: string;
  toCoordinatorResitFlow: string;
  accessToken: string | undefined;
}>({
  toEligible: '',
  toInstruction: '',
  toResitFlow: '',
  toReceipt: '',
  toSummary: '',
  toCoordinatorResitRegister: '',
  toCoordinatorResitFlow: '',
  accessToken: undefined,
});

export const useResitNavigation = () => useContext(ResitNavigationContext);

enum StartingPage {
  LoadingPage = 'Loading',
  InstructionPage = 'Instruction',
  CoordinatorResitRegisterPage = 'CoordinatorResitRegister',
  OpsAdminResit = 'OpsAdminResitApply',
}

interface ResitRouteParams {
  accessLinkToken?: string;
}

const useRedirect = (accessLinkToken: string | undefined) => {
  const { url, path } = useRouteMatch();
  const { pathname } = useLocation();

  // get the pathname when the user first visits path "/resit"
  const [firstEnterPathname, setFirstEnterPathname] = useState(pathname);
  const previousPath = usePrevious(path);
  useEffect(() => {
    if (previousPath !== path) {
      setFirstEnterPathname(pathname);
    }
  }, [path, previousPath, pathname]);

  const { data, isSuccess } = useUserDetails();
  const history = useHistory();

  const [startingPage, setStartingPage] = useState<StartingPage>(StartingPage.LoadingPage);

  useEffect(() => {
    if (isSuccess && data) {
      if (data.data.roles?.includes(Role.candidate)) {
        // candidate check inside
        setStartingPage(StartingPage.InstructionPage);
      } else if (data.data.roles?.includes(Role.coordinator)) {
        // coordinator check inside
        setStartingPage(StartingPage.CoordinatorResitRegisterPage);
      }
    }
  }, [accessLinkToken, data, isSuccess, url]);

  useEffect(() => {
    switch (startingPage) {
      case StartingPage.InstructionPage:
        // Allow candidate to straightly go into receipt/summary page.
        // It's for links sent by e-mail after candidate has registered successfully
        if (
          !firstEnterPathname.includes(`${url}/${receipt.route}`) &&
          !firstEnterPathname.includes(`${url}/${summary.route}`) &&
          !firstEnterPathname.includes(`${url}/${listing.route}`)
        ) {
          history.push(`${url}/${instruction.route}`);
        }
        break;
      case StartingPage.CoordinatorResitRegisterPage:
        // Allow coordinator to go straight to resit listing page
        if (!firstEnterPathname.includes(`${url}/${coordinatorListing.route}`)) {
          history.push(`${url}/${coordinatorResitRegister.route}`);
        }
        break;
      default:
        break;
    }
  }, [startingPage, history, url, firstEnterPathname]);

  return {
    isCandidate: data && data.data.roles?.includes(Role.candidate),
    pathname,
  };
};

const useAccessToken = () => {
  const location = useLocation<ResitRouteParams>();
  const { accessLinkToken } = parseLocationSearch(location.search);

  const [accessToken, setAccessToken] = useState<string | undefined>();

  useEffect(() => {
    if (accessLinkToken) {
      setAccessToken(accessLinkToken);
    }
  }, [accessLinkToken]);

  return accessToken;
};

const ResitRoute = () => {
  const { url, path } = useRouteMatch();
  const { t } = useTranslation();

  const accessToken = useAccessToken();

  const RESIT_INSTRUCTION = t('resit->>ResitRoute.Register Year-End MTL Exam');
  const COORDINATOR_REGISTRATION_TITLE = t('resit->>ResitRoute.Resit Registration');

  const { isCandidate } = useRedirect(accessToken);

  const contextValue = useMemo(
    () => ({
      toEligible: `${url}/${eligible.route}`,
      toInstruction: `${url}/${instruction.route}`,
      toResitFlow: `${url}/${resitFlow.route}`,
      toReceipt: `${url}/${receipt.route}`,
      toSummary: `${url}/${summary.route}`,
      toCoordinatorResitRegister: `${url}/${coordinatorResitRegister.route}`,
      toCoordinatorResitFlow: `${url}/${coordinatorResitFlow.route}`,
      accessToken,
    }),
    [accessToken, url]
  );

  return (
    <ResitNavigationContext.Provider value={contextValue}>
      <Switch>
        {/** Candidate Resit */}
        <PrivateRoute path={`${path}/${listing.route}`} roles={[Role.candidate]}>
          <Listing />
        </PrivateRoute>
        <PrivateRoute path={`${path}/${instruction.route}`} roles={[Role.candidate]}>
          <Instruction title={RESIT_INSTRUCTION} />
        </PrivateRoute>
        <PrivateRoute path={`${path}/${resitFlow.route}`} roles={[Role.candidate]}>
          <ResitFlow title={RESIT_INSTRUCTION} />
        </PrivateRoute>
        <PrivateRoute path={`${path}/${summary.route}`} roles={[Role.candidate]}>
          <Summary />
        </PrivateRoute>
        <PrivateRoute
          path={`${path}/:examRegistrationType/${receipt.route}`}
          roles={[Role.candidate]}
        >
          <Receipt />
        </PrivateRoute>

        {/** Coordinator Resit */}
        <PrivateRoute path={`${path}/${coordinatorResitRegister.route}`} roles={[Role.coordinator]}>
          <CoordinatorResitRegister title={COORDINATOR_REGISTRATION_TITLE} />
        </PrivateRoute>
        <PrivateRoute path={`${path}/${coordinatorResitFlow.route}`} roles={[Role.coordinator]}>
          <CoordinatorResit title={COORDINATOR_REGISTRATION_TITLE} />
        </PrivateRoute>

        <Route path="*" exact>
          {isCandidate ? (
            <Redirect to={`${path}/${instruction.route}`} />
          ) : (
            <Redirect to={`${path}/${coordinatorResitRegister.route}`} />
          )}
        </Route>
      </Switch>
    </ResitNavigationContext.Provider>
  );
};

export default ResitRoute;
